// a component that allows for the editing and visualization of references added for a candidate
import Typography from '@mui/material/Typography';
import { Alert, Button, Snackbar, Tooltip } from '@mui/material';
import styled from 'styled-components';
import React, { useMemo, useState, CSSProperties } from 'react';
import EditableReference from './EditableReference';
import Modal from '@mui/material/Modal';
import { ModalInner } from '../lower-order';
import { isSourceTheSame } from '../../app/util';
import { AddSourceModal } from './AddSourceModal';
import { MediaType } from '../../app/feathers/shared/MediaType';
import { newSource } from './newSource';
import {
  addRemovedLink,
  classifyLink,
  normalizeUrlForComparison,
  restoreRemovedLink,
} from '../../util/sanitize-and-classify-links';
import { Info, Add } from '@mui/icons-material';
import { References, CandidateReferenceCategoryType, CandidateReferenceCategory } from '../../app/feathers/candidates/References';
import { Link } from '../../app/feathers/shared/Link';
import { ScrapedContent } from '../../app/feathers/scraped-content/scraped-content';
import { CandidateDraft, ScrapedContentChange } from '../../app/feathers/candidates/CandidateDraft';
import { Source } from '../../app/feathers/shared/Source';
import { PriorityLevel } from '../../app/feathers/shared/PriorityLevel';

interface ReferenceEditorProps {
  // the best way to update the references is to use the function mergeReferencesWithDraft, in util.js
  onChangeActiveDraft: (draft: Pick<CandidateDraft, 'issues' | 'bios' | 'references'>, scrapedContentChanges?: ScrapedContentChange[]) => void;
  disabled?: boolean;
  style?: CSSProperties;
  activeDraft: CandidateDraft;
  priorityLevel?: PriorityLevel;
  editingEnabled?: boolean;
  scrapedContent?: ScrapedContent[];
  onScrapedContentSaved?: (scrapedContent: ScrapedContent) => void;
  categoryTypesToRender?: CandidateReferenceCategoryType[];
  showSourceMetadata?: boolean;
  onAutoProfileReference?: (
    reference: {
      type: CandidateReferenceCategoryType.website | CandidateReferenceCategoryType.news;
      url: string;
      title?: string;
    }
  ) => Promise<void>;
}

interface CategoryToRender {
  type: CandidateReferenceCategoryType;
  sources: Link[];
}

interface PruneSourcesModalProps {
  contentTypesToPrune: string[];
  onCancel: () => void;
  onConfirm: () => void;
}

export const ReferenceEditor: React.FC<ReferenceEditorProps> = ({
  onChangeActiveDraft,
  disabled,
  style,
  activeDraft,
  priorityLevel,
  editingEnabled = true,
  scrapedContent,
  onScrapedContentSaved,
  showSourceMetadata = true,
  onAutoProfileReference,
  categoryTypesToRender = [
    CandidateReferenceCategoryType.website,
    CandidateReferenceCategoryType.social,
    CandidateReferenceCategoryType.news,
  ]
}) => {

  const references = activeDraft?.references;
  // Get all categories except questionnaire
  const categoriesToRender = useMemo<CategoryToRender[]>(() => {
    return categoryTypesToRender.map(type => ({
      type,
      sources: (references?.categories || []).find(c => c.type === type)?.sources || [],
    }));
  }, [references?.categories, categoryTypesToRender]);

  const sourcesUsedInProfile = useMemo<Source[]>(() => {
    if (!activeDraft) return [];

    const bios = activeDraft.bios || [];
    const issues = activeDraft.issues || [];

    const allSources = [
      ...bios.flatMap(bio => bio.sources || []),
      ...issues.flatMap(issue => (issue.stances || []).flatMap(stance => stance.sources || []))
    ];

    return allSources;
  }, [activeDraft]);

  const [showAddSourceModal, setShowAddSourceModal] = useState<CandidateReferenceCategoryType | null>(null);
  const [autoProfileReference, setAutoProfileReference] = useState<{
    type: CandidateReferenceCategoryType.website | CandidateReferenceCategoryType.news;
    url: string;
    title?: string;
  } | null>(null);
  const [autoProfiling, setAutoProfiling] = useState(false);
  const existingLinks = (references?.categories || []).map(c => (c.sources || [])).flat();
  const [error, setError] = useState<Error | string | null>(null);

  const [removeLinkUnconfirmed, setRemoveLinkUnconfirmed] = useState<{ categoryType: CandidateReferenceCategoryType; index: number } | null>(null);
  const [contentTypesToPrune, setContentTypesToPrune] = useState<string[]>([]);

  const [draggedSource, setDraggedSource] = useState<{ categoryType: CandidateReferenceCategoryType; index: number } | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<CandidateReferenceCategoryType | null>(null);

  const withRemovedLinkTracked = (draft: CandidateDraft, url: string): CandidateDraft => ({
    ...draft,
    removedAutoContent: {
      ...draft.removedAutoContent,
      links: addRemovedLink(draft.removedAutoContent?.links, url),
    },
  });

  const withRemovedLinkRestored = (draft: CandidateDraft, url: string): CandidateDraft => ({
    ...draft,
    removedAutoContent: {
      ...draft.removedAutoContent,
      links: restoreRemovedLink(draft.removedAutoContent?.links, url),
    },
  });

  const removeLinkAtIndex = (categoryType: CandidateReferenceCategoryType, index: number, confirmed: boolean = false): void => {
    const category = (references?.categories || []).find(c => c.type === categoryType);
    const currentLinksForCategory = (category?.sources || []).slice();
    const linkToRemove = currentLinksForCategory[index] as Link | undefined;

    if(!linkToRemove) return;

    currentLinksForCategory.splice(index, 1);

    const categoriesOther = (references?.categories || []).filter(c => c.type !== categoryType);
    const categoryNew: CandidateReferenceCategory = {
      type: categoryType,
      sources: currentLinksForCategory,
      missing: true,
    };
    const categoriesNew = [
      ...categoriesOther,
      categoryNew
    ];

    const mediaType = categoryType;
    const stancesWithRemovedUrl = activeDraft.issues
      .flatMap(({ stances }) => stances)
      .filter(({ sources }) => 
        sources.some(({ url }) => 
          isSourceTheSame(linkToRemove.url, url, mediaType)
        )
      );

    const bioPersonalHasRemovedUrl = activeDraft.bios.some(({ sources, type }) => 
      type === 'personal' && sources.some(({ url }) => 
        isSourceTheSame(linkToRemove.url, url, mediaType)
      )
    );
    const bioPoliticalHasRemovedUrl = activeDraft.bios.some(({ sources, type }) => 
      type === 'political' && sources.some(({ url }) => 
        isSourceTheSame(linkToRemove.url, url, mediaType)
      )
    );
    const bioProfessionalHasRemovedUrl = activeDraft.bios.some(({ sources, type }) => 
      type === 'professional' && sources.some(({ url }) => 
        isSourceTheSame(linkToRemove.url, url, mediaType)
      )
    );

    if(stancesWithRemovedUrl?.length || bioPersonalHasRemovedUrl || bioPoliticalHasRemovedUrl || bioProfessionalHasRemovedUrl) {
      if(confirmed) {
        const updatedDraft: CandidateDraft = withRemovedLinkTracked({
          ...activeDraft,
          references: {
            checked: true,
            categories: categoriesNew.slice(),
            totalSources: categoriesNew.slice().reduce((acc, cat) => acc + (cat.sources?.length || 0), 0)
          }
        }, linkToRemove.url);

        updatedDraft.issues = activeDraft.issues.map(({ stances, ...issue }) => ({
          ...issue,
          stances: stances.filter(stance => !stance.sources.some(({ url }) => isSourceTheSame(linkToRemove.url, url, mediaType)))
        }));
        updatedDraft.bios = activeDraft.bios.map(({ sources, ...bio }) => ({
          ...bio,
          sources: sources.filter(({ url }) => !isSourceTheSame(linkToRemove.url, url, mediaType))
        }));

        // Track scrapedContent deletion when removing a link
        let scrapedContentChanges: ScrapedContentChange[] | undefined;
        if (scrapedContent && linkToRemove.url) {
          const matchingScrapedContent = scrapedContent.find(sc => {
            if (!sc) return false;
            const urlMatches = sc.url && isSourceTheSame(linkToRemove.url, sc.url, mediaType);
            const resolvedUrlMatches = sc.content?.resolvedUrl && isSourceTheSame(linkToRemove.url, sc.content.resolvedUrl, mediaType);
            return urlMatches || resolvedUrlMatches;
          });

          if (matchingScrapedContent) {
            const existingChanges = activeDraft.$scrapedContentChanges || [];
            // Remove any existing change for this scrapedContentId to avoid duplicates
            const filteredChanges = existingChanges.filter(
              change => change.scrapedContentId !== matchingScrapedContent._id
            );
            // Add the delete change
            scrapedContentChanges = [
              ...filteredChanges,
              {
                scrapedContentId: matchingScrapedContent._id,
                changes: {
                  delete: true
                }
              }
            ];
          }
        }

        onChangeActiveDraft(updatedDraft, scrapedContentChanges);
        setRemoveLinkUnconfirmed(null);
        setContentTypesToPrune([]);
      } else {
        const contentTypesToPrune = [
          stancesWithRemovedUrl.length && `${stancesWithRemovedUrl.length} stances`,
          bioPoliticalHasRemovedUrl && 'Political background',
          bioProfessionalHasRemovedUrl && 'Professional background',
          bioPersonalHasRemovedUrl && 'Personal background',
        ].filter(Boolean) as string[];
        setRemoveLinkUnconfirmed({ categoryType, index });
        setContentTypesToPrune(contentTypesToPrune);
        return;
      }
    } else {
      // we have nothing to worry about, we can just remove the link
      const updatedDraft: CandidateDraft = withRemovedLinkTracked({
        ...activeDraft,
        references: {
          checked: true,
          categories: categoriesNew.slice(),
          totalSources: categoriesNew.slice().reduce((acc, cat) => acc + (cat.sources?.length || 0), 0)
        }
      }, linkToRemove.url);

      // Track scrapedContent deletion when removing a link
      let scrapedContentChanges: ScrapedContentChange[] | undefined;
      if (scrapedContent && linkToRemove.url) {
        const matchingScrapedContent = scrapedContent.find(sc => {
          if (!sc) return false;
          const urlMatches = sc.url && isSourceTheSame(linkToRemove.url, sc.url, mediaType);
          const resolvedUrlMatches = sc.content?.resolvedUrl && isSourceTheSame(linkToRemove.url, sc.content.resolvedUrl, mediaType);
          return urlMatches || resolvedUrlMatches;
        });

        if (matchingScrapedContent) {
          const existingChanges = activeDraft.$scrapedContentChanges || [];
          // Remove any existing change for this scrapedContentId to avoid duplicates
          const filteredChanges = existingChanges.filter(
            change => change.scrapedContentId !== matchingScrapedContent._id
          );
          // Add the delete change
          scrapedContentChanges = [
            ...filteredChanges,
            {
              scrapedContentId: matchingScrapedContent._id,
              changes: {
                delete: true
              }
            }
          ];
        }
      }

      onChangeActiveDraft(updatedDraft, scrapedContentChanges);
    }
  }

  // Helper function to update a source at a specific index in a category
  const updateLinkAtIndex = (categoryType: CandidateReferenceCategoryType, index: number, newLink: Link): void => {
    const category = (references?.categories || []).find(c => c.type === categoryType);
    const currentLinksForCategory = (category?.sources || []).slice();
    const previousLink = currentLinksForCategory[index] as Link | undefined;
    if(!previousLink) return;

    // Update: replace the source at this index
    currentLinksForCategory[index] = newLink;

    const categoriesWithout = (references?.categories || []).filter(c => c.type !== categoryType);
    const categoryNew: CandidateReferenceCategory = {
      type: categoryType,
      sources: currentLinksForCategory,
      missing: true,
    };
    const categoriesNew = [
      ...categoriesWithout,
      categoryNew
    ];

    let updatedDraft: CandidateDraft = {
      ...activeDraft,
      references: {
        checked: true,
        categories: categoriesNew.slice(),
        totalSources: categoriesNew.slice().reduce((acc, cat) => acc + (cat.sources?.length || 0), 0)
      }
    };

    // If the link's URL or title changed, update all matching sources in activeDraft
    const urlChanged = previousLink.url !== newLink.url;
    const titleChanged = (previousLink.title || '') !== (newLink.title || '');

    if (urlChanged) {
      updatedDraft = withRemovedLinkRestored(
        withRemovedLinkTracked(updatedDraft, previousLink.url),
        newLink.url
      );
    }

    if (urlChanged || titleChanged) {
      // Determine mediaType based on categoryType for comparison
      const mediaType = categoryType === 'website' ? 'website' : 'other';

      // Update all matching sources in issues/stances
      const issuesUpdated = activeDraft.issues.map(issue => ({
        ...issue,
        stances: (issue.stances || []).map(stance => ({
          ...stance,
          sources: (stance.sources || []).map(source => {
            if (!isSourceTheSame(previousLink.url, source.url, mediaType)) {
              return source;
            }

            // Use intelligent source updating
            return newSource(source, newLink, previousLink);
          }),
        })),
      }));

      // Update all matching sources in bios
      const biosUpdated = activeDraft.bios.map(bio => ({
        ...bio,
        sources: (bio.sources || []).map(source => {
          if (!isSourceTheSame(previousLink.url, source.url, mediaType)) {
            return source;
          }

          // Use intelligent source updating
          return newSource(source, newLink, previousLink);
        }),
      }));

      updatedDraft.issues = issuesUpdated;
      updatedDraft.bios = biosUpdated;
    }

    onChangeActiveDraft(updatedDraft);
  };

  // Helper function to append a source to a category
  const appendLinkToCategory = (categoryType: CandidateReferenceCategoryType, newLink: Link): void => {

    const currentLinks = (references?.categories || []).flatMap(c => c.sources || []).flat();
    
    // Check for duplicates
    const existingUrls = currentLinks.map(s => normalizeUrlForComparison(typeof s === 'string' ? s : (s?.url || ''))).filter(Boolean);
    const newUrl = normalizeUrlForComparison(newLink?.url || '');
    
    if (existingUrls.includes(newUrl)) {
      setError(`The url '${newUrl}' has already been added as a source.`);
      return;
    }

    const currentLinksForCategory = (references?.categories || []).find(c => c.type === categoryType)?.sources || [];
    
    const newLinks = [...currentLinksForCategory, newLink];

    const categoriesWithout = (references?.categories || []).filter(c => c.type !== categoryType);
    const categoryNew: CandidateReferenceCategory = {
      type: categoryType,
      sources: newLinks,
      missing: true,
    };
    const categoriesNew = [
      ...categoriesWithout,
      categoryNew
    ];

    const updatedDraft: CandidateDraft = withRemovedLinkRestored({
      ...activeDraft,
      references: {
        checked: true,
        categories: categoriesNew.slice(),
        totalSources: categoriesNew.slice().reduce((acc, cat) => acc + (cat.sources?.length || 0), 0)
      }
    }, newLink.url);

    onChangeActiveDraft(updatedDraft);
  };

  // Helper function to move a link from one category/index to another category
  const moveLinkToCategory = (
    linkCategoryType: CandidateReferenceCategoryType,
    linkIndex: number,
    targetCategoryType: CandidateReferenceCategoryType
  ): void => {
    // Don't allow moving if target is website and already has a link
    if (targetCategoryType === 'website') {
      const targetCategory = (references?.categories || []).find(c => c.type === targetCategoryType);
      const targetLinks = (targetCategory?.sources || []).filter((s): s is Link => {
        return Boolean(s && typeof s === 'object' && 'url' in s && s.url);
      });
      if (targetLinks.length >= 1) {
        setError('Website category can only have one link.');
        return;
      }
    }

    const linkCategory = (references?.categories || []).find(c => c.type === linkCategoryType);
    const linksInCategory = (linkCategory?.sources || []).slice();
    const linkToMove = linksInCategory[linkIndex] as Link | undefined;

    if (!linkToMove || typeof linkToMove !== 'object' || !('url' in linkToMove)) {
      return;
    }

    // Remove from link category
    const remainingLinks = linksInCategory.filter((_, i) => i !== linkIndex);

    // Add to target category
    const targetCategory = (references?.categories || []).find(c => c.type === targetCategoryType);
    const targetLinks = (targetCategory?.sources || []).slice();
    
    // Check for duplicates in target
    const existingUrls = targetLinks.map(s => normalizeUrlForComparison(typeof s === 'string' ? s : (s?.url || ''))).filter(Boolean);
    const newUrl = normalizeUrlForComparison(linkToMove.url || '');
    
    if (existingUrls.includes(newUrl)) {
      setError(`The url '${newUrl}' has already been added to this category.`);
      return;
    }

    // If moving to social category, validate and classify the mediaType
    let linkToAdd = linkToMove;
    if (targetCategoryType === 'social') {
      const mediaType: MediaType = classifyLink(linkToMove.url, 'other') as MediaType;
      if (mediaType === 'other') {
        setError('This social media type is not supported. Please check your inputs.');
        return;
      }
      // Update the link with the correct mediaType and title
      linkToAdd = {
        ...linkToMove,
        mediaType: mediaType as MediaType,
        title: mediaType.slice(0, 1).toUpperCase() + mediaType.slice(1).toLowerCase()
      };
    } else if (targetCategoryType === 'news') {
      linkToAdd = {
        ...linkToMove,
        mediaType: 'other' as MediaType,
        title: linkToMove.title || 'Article'
      };
    } else if(targetCategoryType === 'website') {
      const { title, ...rest } = linkToMove;
      linkToAdd = {
        ...rest,
        mediaType: 'website' as MediaType
      };

      console.log('linkToAdd', linkToAdd);
    } 

    const newTargetLinks = [...targetLinks, linkToAdd];

    // Update both categories atomically by updating all categories at once
    const allCategories = (references?.categories || []).slice();
    const updatedCategories = allCategories.map(cat => {
      if (cat.type === linkCategoryType) {
        return {
          ...cat,
          sources: remainingLinks,
          missing: true,
        };
      } else if (cat.type === targetCategoryType) {
        return {
          ...cat,
          sources: newTargetLinks,
          missing: true,
        };
      }
      return cat;
    });

    // Update references with both categories changed
    const updatedDraft: CandidateDraft = {
      ...activeDraft,
      references: {
        checked: true,
        categories: updatedCategories,
        totalSources: updatedCategories.reduce((acc, cat) => acc + (cat.sources?.length || 0), 0)
      }
    };

    // Update all matching sources in issues/stances and bios using newSource
    // Determine mediaType based on link category type for comparison
    const mediaType = linkCategoryType === 'website' ? 'website' : 'other';

    // Update all matching sources in issues/stances
    const issuesUpdated = activeDraft.issues.map(issue => ({
      ...issue,
      stances: (issue.stances || []).map(stance => ({
        ...stance,
        sources: (stance.sources || []).map(source => {
          if (!isSourceTheSame(linkToMove.url, source.url, mediaType)) {
            return source;
          }

          // Use newSource to update with the new link (which has updated mediaType and title)
          return newSource(source, linkToAdd, linkToMove);
        }),
      })),
    }));

    // Update all matching sources in bios
    const biosUpdated = activeDraft.bios.map(bio => ({
      ...bio,
      sources: (bio.sources || []).map(source => {
        if (!isSourceTheSame(linkToMove.url, source.url, mediaType)) {
          return source;
        }

        // Use newSource to update with the new link (which has updated mediaType and title)
        return newSource(source, linkToAdd, linkToMove);
      }),
    }));

    updatedDraft.issues = issuesUpdated;
    updatedDraft.bios = biosUpdated;

    // Track scrapedContent changes when recategorizing (only for 'news' and 'website' which are valid ScrapedContentType values)
    if (scrapedContent && linkToMove.url && (targetCategoryType === 'news' || targetCategoryType === 'website')) {
      // Determine mediaType based on link category type for comparison
      const mediaType = linkCategoryType === 'website' ? 'website' : 'other';

      console.log('scrapedContent', scrapedContent);
      
      const matchingScrapedContent = scrapedContent.find(sc => {
        if (!sc) return false;
        // Check both url and content.resolvedUrl using isSourceTheSame
        const urlMatches = sc.url && isSourceTheSame(linkToMove.url, sc.url, mediaType);
        const resolvedUrlMatches = sc.content?.resolvedUrl && isSourceTheSame(linkToMove.url, sc.content.resolvedUrl, mediaType);
        return urlMatches || resolvedUrlMatches;
      });

      console.log('matchingScrapedContent', matchingScrapedContent);

      if (matchingScrapedContent) {
        const existingChanges = activeDraft.$scrapedContentChanges || [];
        // Remove any existing change for this scrapedContentId to avoid duplicates
        const filteredChanges = existingChanges.filter(
          change => change.scrapedContentId !== matchingScrapedContent._id
        );
        // Add the new change
        const newScrapedContentChanges = [
          ...filteredChanges,
          {
            scrapedContentId: matchingScrapedContent._id,
            changes: {
              type: targetCategoryType
            }
          }
        ];

        console.log('newScrapedContentChanges', newScrapedContentChanges);
        onChangeActiveDraft(updatedDraft, newScrapedContentChanges);
        return;
      }
    }

    onChangeActiveDraft(updatedDraft);
  };

  // Helper function to get category display name
  const getCategoryDisplayName = (type: CandidateReferenceCategoryType): string => {
    const names: Record<CandidateReferenceCategoryType, string> = {
      'website': 'Campaign website',
      'social': 'Candidate social media',
      'news': 'Articles',
      'official-website': 'Official website',
      'other': 'Other sources',
      'questionnaire': 'Questionnaire',
    };
    return names[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Helper function to check if category should show "Optional" tooltip
  const shouldShowOptionalTooltip = (type: CandidateReferenceCategoryType): boolean => {
    return type === 'news' && priorityLevel === 'low';
  };

  const errorMessage = useMemo<string>(() => {
    if(typeof error === 'string') return error;
    if(typeof error === 'object' && error !== null) {
      const errorObj = error as { type?: string; errors?: Record<string, unknown>; message?: string; code?: number };
      if(errorObj.type === 'FeathersError') {
        let messageToShow: string | undefined;
        if(errorObj.errors && Object.values(errorObj.errors).length > 0) {
          messageToShow = Object.values(errorObj.errors).join(' ') as string;
        } else {
          messageToShow = errorObj.message;
        }
        switch (errorObj.code) {
          case 400:
            if(messageToShow) return messageToShow;
            return 'There was a problem with the data you submitted. Please check the form and try again.';
          case 401:
          case 403:
            if(messageToShow) return messageToShow;
            return 'You are not authorized to perform this action.';
          default:
            return messageToShow || 'There was an error when saving. Please try again!';
        }
      } else return errorObj.message || 'There was an error when saving. Please try again!';
    }

    return 'There was an error when saving. Please try again!';
  }, [error]);

  return (
    <Wrapper style={style}>
      <Modal
        open={Boolean(showAddSourceModal)}
        onClose={() => setShowAddSourceModal(null)}
      >
        <ModalInner
          style={{
            minHeight: 'auto',
            width: '400px'
          }}
        >
          <AddSourceModal
            type={showAddSourceModal}
            existingLinks={existingLinks}
            onAdd={(newSource) => {
              if(!showAddSourceModal) return;
              const categoryType = showAddSourceModal;
              appendLinkToCategory(categoryType, newSource);
              setShowAddSourceModal(null);
              if(
                onAutoProfileReference &&
                (
                  categoryType === CandidateReferenceCategoryType.website ||
                  categoryType === CandidateReferenceCategoryType.news
                )
              ) {
                setAutoProfileReference({
                  type: categoryType,
                  url: newSource.url,
                  ...(newSource.title ? { title: newSource.title } : {}),
                });
              }
            }}
            onCancel={() => setShowAddSourceModal(null)}
          />
        </ModalInner>
      </Modal>
      <Modal
        open={Boolean(autoProfileReference)}
        onClose={() => {
          if(!autoProfiling) setAutoProfileReference(null);
        }}
      >
        <ModalInner
          style={{
            minHeight: 'auto',
            width: '500px'
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gridGap: '16px' }}>
            <Typography variant={'h4'} style={{ fontSize: '18px', fontWeight: 600 }}>
              Regenerate this candidate profile?
            </Typography>
            <Typography variant={'body1'}>
              You&apos;ve added a source that wasn&apos;t included in the profiling. Should this profile be regenerated? All other manual changes you&apos;ve made will be discarded.
            </Typography>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                disabled={autoProfiling}
                onClick={() => setAutoProfileReference(null)}
              >
                No, profile manually
              </Button>
              <Button
                variant={'contained'}
                disabled={autoProfiling || !autoProfileReference}
                onClick={async () => {
                  if(!autoProfileReference || !onAutoProfileReference) return;
                  setAutoProfiling(true);
                  setError(null);
                  try {
                    await onAutoProfileReference(autoProfileReference);
                    setAutoProfileReference(null);
                  } catch (err) {
                    setError(err as Error);
                  } finally {
                    setAutoProfiling(false);
                  }
                }}
              >
                {autoProfiling ? 'Starting auto profile…' : 'Yes, auto profile'}
              </Button>
            </div>
          </div>
        </ModalInner>
      </Modal>
      <Modal
        open={Boolean(removeLinkUnconfirmed)}
        onClose={() => {
          setContentTypesToPrune([]);
          setRemoveLinkUnconfirmed(null);
        }}
      >
        <ModalInner
          style={{
            minHeight: 'auto',
            width: '400px'
          }}
        >
          <PruneSourcesModal
            contentTypesToPrune={contentTypesToPrune}
            onCancel={() => setRemoveLinkUnconfirmed(null)}
            onConfirm={() => {
              if(!removeLinkUnconfirmed) return;
              removeLinkAtIndex(removeLinkUnconfirmed?.categoryType, removeLinkUnconfirmed?.index, true);
            }}
          />
        </ModalInner>
      </Modal>
      {categoriesToRender.map((category) => {
        const categoryType = category.type;
        const categorySources = (category.sources || []).filter((s): s is Link => {
          return Boolean(s && typeof s === 'object' && 'url' in s && s.url);
        });
        const maxEntries = categoryType === 'website' ? 1 : Infinity;
        const canAddMore = categorySources.length < maxEntries;

        return (
          <Section key={categoryType} condensed={1}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
              <Typography variant={'h4'} style={{ fontSize: '16px' }}>
                {getCategoryDisplayName(categoryType)}
              </Typography>
              {shouldShowOptionalTooltip(categoryType) && (
                <>
                  <Typography variant={'body2'} style={{ opacity: 1.0, marginLeft: '12px' }}>
                    Optional
                  </Typography>
                  <Tooltip
                    title="This is a low priority candidate. To save time, you can avoid doing a news search on them.">
                    <Info style={{ height: '18px', width: '18px', color: '#888888', marginBottom: '2px' }}/>
                  </Tooltip>
                </>
              )}
            </div>
              <div
                style={{
                  paddingTop: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  minHeight: '40px',
                  border: dragOverCategory === categoryType ? '2px dashed #1976d2' : '2px dashed transparent',
                  borderRadius: '4px',
                  padding: dragOverCategory === categoryType ? '10px' : '0',
                  transition: 'all 0.2s ease'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (editingEnabled && draggedSource && draggedSource.categoryType !== categoryType) {
                    setDragOverCategory(categoryType);
                  }
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Only clear if we're actually leaving the drop zone
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX;
                  const y = e.clientY;
                  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                    setDragOverCategory(null);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverCategory(null);
                  if (editingEnabled && draggedSource && draggedSource.categoryType !== categoryType) {
                    moveLinkToCategory(draggedSource.categoryType, draggedSource.index, categoryType);
                  }
                  setDraggedSource(null);
                }}
              >
                {categorySources.length > 0 ? categorySources.map((source, index) => {
                  if (!source?.url) return null;
                  const isDragging = draggedSource?.categoryType === categoryType && draggedSource?.index === index;
                  return (
                    <div
                      key={index}
                      draggable={editingEnabled}
                      onDragStart={(e) => {
                        if (editingEnabled) {
                          setDraggedSource({ categoryType, index });
                          e.dataTransfer.effectAllowed = 'move';
                          // Set a custom data type to identify this as a source drag
                          e.dataTransfer.setData('text/plain', `${categoryType}-${index}`);
                        }
                      }}
                      onDragEnd={() => {
                        setDraggedSource(null);
                        setDragOverCategory(null);
                      }}
                      style={{
                        opacity: isDragging ? 0.5 : 1,
                        cursor: editingEnabled ? 'grab' : 'default',
                        transition: 'opacity 0.2s ease'
                      }}
                    >
                      <EditableReference
                        value={source}
                        onChange={editingEnabled ? (link) => {
                          updateLinkAtIndex(categoryType, index, link);
                        } : undefined}
                        onDelete={editingEnabled ? () => {
                          removeLinkAtIndex(categoryType, index);
                        } : undefined}
                        sourcesUsedInProfile={sourcesUsedInProfile}
                        scrapedContent={scrapedContent}
                        onScrapedContentSaved={onScrapedContentSaved}
                        editingEnabled={editingEnabled}
                        showSourceMetadata={showSourceMetadata}
                        compareUsageByHostname={categoryType === CandidateReferenceCategoryType.website}
                      />
                    </div>
                  );
                }) : (
                  <Typography variant={'body1'}>No {getCategoryDisplayName(categoryType).toLowerCase()} added yet.</Typography>
                )}
                 {editingEnabled && canAddMore && (
                   <Button
                    size="small"
                     startIcon={<Add />}
                     onClick={() => setShowAddSourceModal(categoryType)}
                     style={{ marginTop: '-12px', alignSelf: 'flex-start' }}
                   >
                     Add link
                   </Button>
                 )}
              </div>
          </Section>
        );
      })}
      <Snackbar
        open={Boolean(error)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={6000}
        style={{ position: 'absolute' }}
        onClose={() => setError(null)}
      >
        <Alert
          severity="error"
          onClose={() => setError(null)}
          elevation={6}
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

interface SectionProps {
  condensed?: number;
}

const Section = styled.div<SectionProps>`
  ${props => props.condensed ? `margin-bottom: 36px;` : `margin-bottom: 72px;`}

  display: flex;
  flex-direction: column;
  align-items: stretch h4 {
  ${props => props.condensed ? `margin-bottom: 4px;` : `margin-bottom: 8px;`}
}
`;

const PruneSourcesModal: React.FC<PruneSourcesModalProps> = ({
  contentTypesToPrune,
  onCancel,
  onConfirm,
}) => {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr', gridGap: '12px' }}>
    <Typography variant={'h4'} style={{ fontSize: '18px', fontWeight: 600 }}>Warning!</Typography>
    <Typography variant="body1" style={{ }}>
      Removing this source will remove the following content:
    </Typography>
    <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: 0, paddingLeft: '20px' }}>
      {
        contentTypesToPrune.map((content, idx) => <li key={idx} style={{ margin: 0 }}>
          <Typography variant={'body1'}>
            {content}
          </Typography>
        </li>)
      }
    </ul>
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '8px',
    }}>
      <Button
        variant="text"
        onClick={() => onCancel()}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={() => onConfirm()}
      >
        Confirm
      </Button>
    </div>
  </div>;
};


const areReferencesChecked = (categories: CandidateReferenceCategory[], priorityLevel?: PriorityLevel): boolean => {
  const catRef = categories || [];
  // check conditions for each category
  const websiteCat = catRef.find(c => c.type === 'website');
  const websiteValid = (
    (websiteCat?.sources || []).filter(s => {
      const url = typeof s === 'string' ? s : s?.url;
      return (url || '').trim().length > 0;
    }).length === 1 ||
    websiteCat?.missing
  );
  const socialCat = catRef.find(c => c.type === 'social');
  const socialsValid = (
    (socialCat?.sources || []).filter(s => {
      const url = typeof s === 'string' ? s : s?.url;
      return (url || '').trim().length > 0;
    }).length > 0 ||
    socialCat?.missing
  );
  const newsCat = catRef.find(c => c.type === 'news');
  const newsValid = (
    (newsCat?.sources || []).filter(s => {
      const url = typeof s === 'string' ? s : s?.url;
      return (url || '').trim().length > 0;
    }).length > 0 ||
    newsCat?.missing
  ) || priorityLevel === 'low';

  return (
    Boolean(websiteValid) &&
    Boolean(socialsValid) &&
    Boolean(newsValid)
  );
};
